import { useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEraser } from "@fortawesome/free-solid-svg-icons";
import { useLocalization } from "@progress/kendo-react-intl";
import { useToast } from "@chakra-ui/react";
import { enMessages } from "../../../messages/en-US";
import useIdentityStore from "../../../Zustand/identityStore";
import GenericConfirmationDialog from "./Dialogs/GenericConfirmationDialog";

interface Props {
  /** Runs the actual cache delete call. Should throw on failure. */
  onClear: () => Promise<void>;
  /** Confirmation dialog body — tells the user what exactly gets wiped. */
  confirmationMessage: string;
}

/**
 * SuperAdmin-only button that clears the compliance cache and refreshes the page data.
 * Renders nothing for any other role.
 */
export default function ClearCacheButton({ onClear, confirmationMessage }: Props) {
  const localizationService = useLocalization();
  const isSuperAdmin = useIdentityStore((x) => x.user?.profile?.role) === "SuperAdmin";
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const toast = useToast({ position: "bottom", duration: 5000, isClosable: true });

  const clearCacheL = localizationService.toLanguageString('custom.clearCache', enMessages.custom.clearCache);

  if (!isSuperAdmin) {
    return null;
  }

  const handleConfirm = async () => {
    setIsClearing(true);
    try {
      await onClear();
      toast({
        status: "success",
        description: localizationService.toLanguageString('custom.clearCacheSuccess', enMessages.custom.clearCacheSuccess),
      });
    } catch {
      toast({
        title: localizationService.toLanguageString('custom.error', enMessages.custom.error),
        status: "error",
        description: localizationService.toLanguageString('custom.clearCacheError', enMessages.custom.clearCacheError),
      });
    } finally {
      setIsClearing(false);
    }
  };

  return (
    <>
      <Tooltip anchorElement="target" position="bottom" parentTitle={true}>
        <Button
          disabled={isClearing}
          onClick={() => setConfirmOpen(true)}
          title={clearCacheL}
        >
          {/* text-base keeps it the same 16px as the Kendo svg icons on neighbouring buttons */}
          <FontAwesomeIcon icon={faEraser} className="text-base" />
        </Button>
      </Tooltip>
      <GenericConfirmationDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirm}
        header={clearCacheL}
        body={confirmationMessage}
        cancel={localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel)}
        confirm={localizationService.toLanguageString('custom.confirm', enMessages.custom.confirm)}
        confirmButtonTheme="primary"
      />
    </>
  );
}
